import get from "lodash/get";

import { getPageType } from "../../../lib/analytics/utils";
import { IRIS_TIMEOUT } from "../constants";
import { getPrimarySectionId } from "./getPrimarySectionId";
import { getUserAgent, joinArray } from "../../../../common/utils";
import { isLoopingVideo } from "../../utils";
import { getCookie } from "../../../../common/cookie-util";
import { checkIfVideoTypeLive } from "../../utils/liveVideoUtils";
import { OrcaType } from "../../../globals";

// Determine ad settings from ANS/config/video state variables/default constants
export const getAdSettings = (
  config,
  video,
  videoElementRef,
  isVertical,
  isMuted,
  isLive,
  isPreroll,
  isPlaythrough
) => {
  const {
    additional_properties: {
      advertising: { videoAdZone } = {
        playAds: false,
        playVideoAds: false,
        videoAdZone: ""
      }
    } = {
      advertising: {}
    }
  } = video || {};
  const {
    adOverrides: {
      forceAd = false,
      adZoneOverride = null,
      customParamsOverride = [],
      adTagOverride,
      adTimeoutOverride,
      preloadAd = false,
      skip,
      skipmin,
      skipafter
    } = {},
    debugZeus
  } = config;

  const decoratedAdZone = getVideoAdZone(videoAdZone, forceAd, adZoneOverride);
  const orcaType = getOrcaType(video, config);

  const adSettings = {
    // timeout length for ad request
    adTimeout: adTimeoutOverride || IRIS_TIMEOUT,
    videoAdZone: decoratedAdZone,
    forceAd,
    customParamsOverride,
    adTagOverride,
    preloadAd,
    uuid: video._id,
    debugZeus: debugZeus,
    isVertical: isVertical,
    isMuted: isPreroll ? true : isMuted,
    playerWidth: getPlayerWidth(videoElementRef),
    playerHeight: getPlayerHeight(videoElementRef),
    sections: video.taxonomy?.sections || [],
    playerType: getPlayerType(video),
    isLive: isLive,
    isPreroll: isPreroll,
    isPlaythrough: isPlaythrough,
    pageType: getPageType(),
    orcaType,
    videoConfig: {
      ...(skip && { skip: skip }),
      ...(skipmin && { skipmin: skipmin }),
      ...(skipafter && { skipafter: skipafter })
    },
    primarySectionId: getPrimarySectionId(video, "contentApi"),
    avTags: joinArray(video.taxonomy?.tags, ";", "", "text")
  };

  return adSettings;
};

export const getOrcaType = (video, config) => {
  const carouselPlayer = !!config?.isCarousel;
  const livePlayer = checkIfVideoTypeLive(video);
  const loopingPlayer = isLoopingVideo(video);

  return carouselPlayer
    ? OrcaType.carousel
    : livePlayer
    ? OrcaType.live
    : loopingPlayer
    ? OrcaType.looping
    : OrcaType.vod;
};

export const getPlayerType = video => {
  if (typeof window === "undefined") return;
  let playerType = `posttv-embed`;

  //looping vid
  if (isLoopingVideo(video)) {
    playerType = "posttv-embed-LoopingVideo";
    // we're on homepage
  } else if (
    get(window, "wp_meta_data.isHomepage") === true ||
    get(window, "Fusion.metas.contentType.value") === "homepage"
  ) {
    playerType = "posttv-embed-hp";
    // we're in an iframe
  } else if (window.self !== window.top) {
    playerType = "posttv-embed-iframe";
    // particular leaf page
  } else if (window.wp_pb && /video-leaf-page/.test(window.wp_pb.pageName)) {
    playerType = "posttv-leaf";
  }
  return playerType;
};

const getVideoAdZone = (videoAdZone, forceAd, adZoneOverride) => {
  if (adZoneOverride) return adZoneOverride;
  if (forceAd) return `wpni.video.test/appsalwayson`;
  return getDecoratedAdZone(videoAdZone);
};

const getPlayerWidth = videoElementRef => videoElementRef?.current?.clientWidth;

const getPlayerHeight = videoElementRef =>
  videoElementRef?.current?.clientHeight;

/**
 * @description Adds mobile/tablet suffix to our adZones when neccessary. On mobile, we add "_mweb" to all adZones, on tablet we add "_tablet"
 * @param {Object} adZone - the base adZone, provided by the video ANS
 * @returns {string} the full adZone including necessaary suffix
 */
const getDecoratedAdZone = adZone =>
  adZone?.includes("_") || adZone?.includes("/")
    ? adZone
    : adZone + getAdZoneSuffix();

export const getAdZoneSuffix = () => {
  // we trust akamai supplied devicetype if it exists
  const wapoDeviceTypeCookie = getCookie("wp_devicetype");
  if (wapoDeviceTypeCookie) {
    return wapoDeviceTypeCookie === "2"
      ? "_tablet"
      : wapoDeviceTypeCookie === "1"
      ? "_mweb"
      : "";
  }
  // otherwise we default to trusting navigator.userAgent
  const userAgent = getUserAgent();
  const isIOS = /ipod|iphone|ipad/i.test(userAgent);
  const isTablet = /tablet|ipad/i.test(userAgent);
  // Check for iOS included here to accomodate iOS WKWebViews with custom app names, which do not have "Mobile" in their UA strings
  const isMobile = !isTablet && (isIOS || /[^-]mobi/i.test(userAgent));
  if (isTablet) return "_tablet";
  if (isMobile) return "_mweb";

  return "";
};
